import { useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Chip, Collapse, IconButton, InputAdornment, List, ListItemButton,
  ListSubheader, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchAdminConfigs, selectAdminConfigs, selectAdminLoading,
} from '../../reducers/Beacon4PerformanceAdminReducer'

const TECH_ORDER = ['2G', '3G', '4G', '5G']
const DOT_ACTIVE   = '#2e7d32'
const DOT_INACTIVE = '#bdbdbd'

/**
 * Master pane of the KPI admin. Search at the top, a tech-grouped tree of KPIs
 * with per-tech counts and a status dot, and a "New KPI" action pinned to the
 * bottom. Selecting a row drives the detail pane.
 */
export default function Beacon4PerformanceAdminKpiList({ selectedId, onSelect, onNew }) {
  const dispatch = useDispatch()
  const configs  = useSelector(selectAdminConfigs)
  const loading  = useSelector(selectAdminLoading)

  const [search, setSearch]       = useState('')
  const [collapsed, setCollapsed] = useState({})

  useEffect(() => { dispatch(fetchAdminConfigs({})) }, [dispatch])

  const groups = useMemo(() => {
    const s = search.trim().toLowerCase()
    const match = c => !s ||
      (c.kpiKey  ?? '').toLowerCase().includes(s) ||
      (c.kpiName ?? '').toLowerCase().includes(s) ||
      (c.viewName ?? '').toLowerCase().includes(s)

    const byTech = {}
    configs.filter(match).forEach(c => { (byTech[c.tech] ??= []).push(c) })
    Object.values(byTech).forEach(list =>
      list.sort((a, b) =>
        (a.displayOrder - b.displayOrder) ||
        (a.kpiName ?? '').localeCompare(b.kpiName ?? '')))
    return byTech
  }, [configs, search])

  const techs = useMemo(() => {
    const known = TECH_ORDER.filter(t => groups[t]?.length)
    const extra = Object.keys(groups).filter(t => !TECH_ORDER.includes(t)).sort()
    return [...known, ...extra]
  }, [groups])

  const toggle = (t) => setCollapsed(c => ({ ...c, [t]: !c[t] }))

  return (
    <Box sx={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column',
               borderRight: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      {/* Search */}
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          fullWidth size="small" placeholder="Search KPIs" value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>
            ),
            sx: { fontSize: 12 },
          }}
        />
      </Box>

      {/* Tree */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading && (
          <Typography sx={{ p: 2, fontSize: 11, color: 'text.disabled' }}>Loading…</Typography>
        )}
        {!loading && techs.length === 0 && (
          <Typography sx={{ p: 2, fontSize: 11, color: 'text.disabled' }}>No KPIs match your search.</Typography>
        )}

        {techs.map(tech => {
          const list = groups[tech] ?? []
          const open = !collapsed[tech]
          return (
            <List key={tech} dense disablePadding subheader={
              <ListSubheader
                disableSticky
                onClick={() => toggle(tech)}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                      lineHeight: '32px', fontSize: 12, fontWeight: 700, color: 'text.primary',
                      bgcolor: 'transparent', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); toggle(tech) }}>
                  {open ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />}
                </IconButton>
                {tech}
                <Box sx={{ flex: 1 }} />
                <Chip label={list.length} size="small" sx={{ height: 18, fontSize: 10 }} />
              </ListSubheader>
            }>
              <Collapse in={open} timeout="auto" unmountOnExit>
                {list.map(c => (
                  <ListItemButton
                    key={c.id}
                    selected={c.id === selectedId}
                    onClick={() => onSelect(c.id)}
                    sx={{
                      pl: 3.5, pr: 1.5, py: 0.5,
                      display: 'grid',
                      gridTemplateColumns: 'auto minmax(72px, max-content) minmax(0, 1fr)',
                      columnGap: 1.25, alignItems: 'center',
                      '&.Mui-selected': { bgcolor: 'action.selected' },
                      borderLeft: '2px solid',
                      borderColor: c.id === selectedId ? 'primary.main' : 'transparent',
                    }}
                  >
                    {/* Status dot */}
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%',
                               bgcolor: c.isActive ? DOT_ACTIVE : DOT_INACTIVE }} />
                    {/* KPI key — monospace, sized to content, capped so super-long keys still ellipsize */}
                    <Typography
                      noWrap title={c.kpiKey}
                      sx={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600,
                            color: 'text.primary' }}
                    >
                      {c.kpiKey}
                    </Typography>
                    {/* Display name — fills remaining space and ellipsizes */}
                    <Typography
                      noWrap title={c.kpiName}
                      sx={{ fontSize: 11, color: 'text.secondary', minWidth: 0 }}
                    >
                      {c.kpiName}
                    </Typography>
                  </ListItemButton>
                ))}
              </Collapse>
            </List>
          )
        })}
      </Box>

      {/* New KPI */}
      <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button fullWidth startIcon={<AddIcon />} variant="outlined" size="small"
          onClick={onNew} sx={{ textTransform: 'none' }}>
          New KPI
        </Button>
      </Box>
    </Box>
  )
}